import { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  Github,
  Trash2,
  Pencil,
  CornerDownRight,
  ChevronUp,
  ChevronDown,
  MessageCircle,
} from "lucide-react";
import { supabase } from "@/lib/supabase";

// Site owner. Lets the admin moderate (delete) any comment.
const ADMIN_ID = "2432d3b7-83e4-4c4b-a546-63ccc0c5e79c";

function timeAgo(iso) {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" });
}

function profileOf(user) {
  if (!user) return null;
  const m = user.user_metadata || {};
  return {
    id: user.id,
    name: m.user_name || m.preferred_username || m.name || "github user",
    avatar: m.avatar_url || "",
  };
}

// Render @mentions inline. Only the mention that matches the current
// viewer turns blue, so each person sees when *they* were mentioned.
function renderBody(text, myName) {
  const re = /@([a-zA-Z0-9-]{1,39})/g;
  const out = [];
  let last = 0;
  let m;
  let key = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const uname = m[1];
    const mine = myName && uname.toLowerCase() === myName.toLowerCase();
    if (mine) {
      out.push(
        <span key={key++} className="text-sky-400 font-medium">
          @{uname}
        </span>
      );
    } else {
      out.push(`@${uname}`);
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

function friendlyError(err) {
  const msg = err?.message || "Something went wrong";
  if (/inappropriate language/i.test(msg))
    return "Your comment was blocked for inappropriate language.";
  return msg;
}

export default function Comments({ slug }) {
  const [comments, setComments] = useState([]);
  const [votes, setVotes] = useState({}); // id -> { score, mine }
  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState(null);
  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    const { data: cdata } = await supabase
      .from("comments")
      .select("*")
      .eq("post_slug", slug)
      .order("created_at", { ascending: true });
    const list = cdata || [];
    setComments(list);

    const ids = list.map((c) => c.id);
    const myId = (await supabase.auth.getUser()).data?.user?.id;
    if (ids.length) {
      const { data: vdata } = await supabase
        .from("comment_votes")
        .select("comment_id,value,user_id")
        .in("comment_id", ids);
      const map = {};
      (vdata || []).forEach((v) => {
        const e = map[v.comment_id] || { score: 0, mine: 0 };
        e.score += v.value;
        if (v.user_id === myId) e.mine = v.value;
        map[v.comment_id] = e;
      });
      setVotes(map);
    } else {
      setVotes({});
    }
    setLoading(false);
  }, [slug]);

  useEffect(() => {
    load();
    supabase.auth.getUser().then(({ data }) => setMe(profileOf(data?.user)));
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      setMe(profileOf(session?.user));
    });
    return () => sub?.subscription?.unsubscribe();
  }, [load]);

  const signIn = async () => {
    setError("");
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "github",
      options: {
        scopes: "read:user",
        // Clean URL only (no hash/query). A leftover #fragment or stale
        // token in window.location.href produced a malformed "##access_token"
        // redirect that broke session detection for some users.
        redirectTo: window.location.origin + window.location.pathname,
      },
    });
    if (error) setError(error.message);
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setMe(null);
  };

  const addComment = async (body, parentId) => {
    const clean = body.trim();
    if (!clean) return false;
    setPosting(true);
    setError("");
    const { data, error } = await supabase
      .from("comments")
      .insert({ post_slug: slug, body: clean, parent_id: parentId || null })
      .select()
      .single();
    setPosting(false);
    if (error) {
      setError(friendlyError(error));
      return false;
    }
    setComments((c) => [...c, data]);
    return true;
  };

  const submitTop = async (e) => {
    e.preventDefault();
    if (await addComment(text)) setText("");
  };

  const saveEdit = async (id, body) => {
    const clean = body.trim();
    if (!clean) return false;
    setError("");
    const { data, error } = await supabase
      .from("comments")
      .update({ body: clean })
      .eq("id", id)
      .select()
      .single();
    if (error) {
      setError(friendlyError(error));
      return false;
    }
    setComments((c) => c.map((x) => (x.id === id ? data : x)));
    setEditing(null);
    return true;
  };

  const remove = async (id) => {
    const prev = comments;
    setComments((c) => c.filter((x) => x.id !== id && x.parent_id !== id));
    const { error } = await supabase.from("comments").delete().eq("id", id);
    if (error) {
      setError(friendlyError(error));
      setComments(prev);
    }
  };

  const vote = async (id, value) => {
    if (!me) return signIn();
    const cur = votes[id] || { score: 0, mine: 0 };
    let next;
    if (cur.mine === value) {
      // toggle off
      next = { score: cur.score - value, mine: 0 };
      setVotes((v) => ({ ...v, [id]: next }));
      await supabase
        .from("comment_votes")
        .delete()
        .eq("comment_id", id)
        .eq("user_id", me.id);
    } else {
      next = { score: cur.score - cur.mine + value, mine: value };
      setVotes((v) => ({ ...v, [id]: next }));
      await supabase
        .from("comment_votes")
        .upsert(
          { comment_id: id, user_id: me.id, value },
          { onConflict: "comment_id,user_id" }
        );
    }
  };

  // Build a 2-level tree: top-level comments with their replies.
  const tree = useMemo(() => {
    const byId = {};
    comments.forEach((c) => (byId[c.id] = c));
    // Resolve any comment to its top-level ancestor so deeper nesting
    // (or stale data) still renders flat under the right thread.
    const rootOf = (c) => {
      let cur = c;
      let guard = 0;
      while (cur.parent_id && byId[cur.parent_id] && guard < 50) {
        cur = byId[cur.parent_id];
        guard += 1;
      }
      return cur;
    };
    const children = {};
    const tops = [];
    comments.forEach((c) => {
      if (c.parent_id) {
        const root = rootOf(c);
        (children[root.id] = children[root.id] || []).push(c);
      } else {
        tops.push(c);
      }
    });
    tops.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    return { tops, children };
  }, [comments]);

  const count = comments.length;

  const nodeProps = {
    me,
    votes,
    vote,
    onReply: setReplyTo,
    replyTo,
    onCancelReply: () => setReplyTo(null),
    onSubmitReply: async (parentId, body) => {
      if (await addComment(body, parentId)) setReplyTo(null);
    },
    editing,
    onEdit: setEditing,
    onCancelEdit: () => setEditing(null),
    onSaveEdit: saveEdit,
    onDelete: remove,
    posting,
  };

  return (
    <section
      id="comments"
      data-testid="comments-section"
      className="py-16 md:py-20 border-t border-white/10"
    >
      <div className="max-w-3xl mx-auto px-6 md:px-12">
        <div className="flex items-center gap-3 text-sm text-zinc-500">
          <MessageCircle className="w-4 h-4" strokeWidth={1.5} />
          <span>
            {count} {count === 1 ? "comment" : "comments"}
          </span>
        </div>

        {/* Composer */}
        <div className="mt-6">
          {me ? (
            <form onSubmit={submitTop} data-testid="comment-form">
              <div className="flex items-center gap-3 mb-3">
                {me.avatar && (
                  <img
                    src={me.avatar}
                    alt={me.name}
                    className="w-7 h-7 rounded-full border border-white/10"
                  />
                )}
                <span className="text-sm text-zinc-400">
                  Commenting as <span className="text-white">@{me.name}</span>
                </span>
                <button
                  type="button"
                  onClick={signOut}
                  className="ml-auto text-xs text-zinc-500 hover:text-white transition-colors"
                >
                  Sign out
                </button>
              </div>
              <textarea
                data-testid="comment-input"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Share a thought…"
                rows={3}
                maxLength={2000}
                className="w-full bg-white/[0.02] border border-white/10 focus:border-white/30 rounded-2xl px-4 py-3 text-sm text-white placeholder:text-zinc-500 outline-none resize-y transition-colors"
              />
              <div className="mt-3 flex items-center justify-between">
                <span className="text-xs text-zinc-600">
                  Only your GitHub handle and avatar are shown. No email.
                </span>
                <button
                  type="submit"
                  disabled={posting || !text.trim()}
                  data-testid="comment-submit"
                  className="text-sm font-medium text-black bg-white px-4 py-2 rounded-full hover:scale-[1.04] transition-transform disabled:opacity-40 disabled:hover:scale-100"
                >
                  {posting ? "Posting…" : "Post comment"}
                </button>
              </div>
            </form>
          ) : (
            <button
              onClick={signIn}
              data-testid="comment-signin"
              className="inline-flex items-center gap-2 bg-white/[0.03] border border-white/10 hover:border-white/30 text-white px-5 py-2.5 rounded-full text-sm transition-colors"
            >
              <Github className="w-4 h-4" strokeWidth={1.75} />
              Sign in with GitHub to comment
            </button>
          )}
          {error && (
            <div className="mt-3 text-sm text-red-400" data-testid="comment-error">
              {error}
            </div>
          )}
        </div>

        {/* List */}
        <div className="mt-10 space-y-8">
          {loading ? (
            <div className="text-sm text-zinc-500">Loading comments…</div>
          ) : tree.tops.length === 0 ? (
            <div className="text-sm text-zinc-500">No comments yet. Be the first.</div>
          ) : (
            tree.tops.map((c) => (
              <CommentNode
                key={c.id}
                comment={c}
                replies={tree.children[c.id] || []}
                {...nodeProps}
              />
            ))
          )}
        </div>
      </div>
    </section>
  );
}

function CommentNode({
  comment: c,
  replies,
  me,
  votes,
  vote,
  onReply,
  replyTo,
  onCancelReply,
  onSubmitReply,
  editing,
  onEdit,
  onCancelEdit,
  onSaveEdit,
  onDelete,
  posting,
  isReply = false,
  rootId,
}) {
  const v = votes[c.id] || { score: 0, mine: 0 };
  const isOwner = me?.id === c.user_id;
  const isAdmin = me?.id === ADMIN_ID;
  const canEdit = isOwner;
  const canDelete = isOwner || isAdmin;
  const isEditing = editing === c.id;
  const isReplying = replyTo === c.id;
  // Replies stay one level deep: a reply-to-a-reply attaches to the same
  // top-level thread, with an @mention pointing at who it answers.
  const replyParentId = isReply ? rootId : c.id;

  const [editText, setEditText] = useState(c.body);
  const [replyText, setReplyText] = useState(isReply ? `@${c.author_name} ` : "");

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      data-testid={`comment-${c.id}`}
      className="flex gap-3"
    >
      {/* Vote rail */}
      <div className="flex flex-col items-center gap-0.5 pt-0.5 shrink-0">
        <button
          onClick={() => vote(c.id, 1)}
          data-testid={`comment-upvote-${c.id}`}
          aria-label="Upvote"
          className={`transition-colors ${
            v.mine === 1 ? "text-emerald-400" : "text-zinc-600 hover:text-white"
          }`}
        >
          <ChevronUp className="w-4 h-4" strokeWidth={2} />
        </button>
        <span
          className={`text-xs tabular-nums ${
            v.score > 0 ? "text-emerald-400" : v.score < 0 ? "text-red-400" : "text-zinc-500"
          }`}
        >
          {v.score}
        </span>
        <button
          onClick={() => vote(c.id, -1)}
          data-testid={`comment-downvote-${c.id}`}
          aria-label="Downvote"
          className={`transition-colors ${
            v.mine === -1 ? "text-red-400" : "text-zinc-600 hover:text-white"
          }`}
        >
          <ChevronDown className="w-4 h-4" strokeWidth={2} />
        </button>
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-start gap-3">
          {c.author_avatar ? (
            <img
              src={c.author_avatar}
              alt={c.author_name}
              className="w-8 h-8 rounded-full border border-white/10 shrink-0"
            />
          ) : (
            <div className="w-8 h-8 rounded-full bg-white/10 shrink-0" />
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 text-sm flex-wrap">
              <a
                href={`https://github.com/${c.author_name}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-white font-medium hover:underline"
              >
                @{c.author_name}
              </a>
              <span className="text-zinc-600">·</span>
              <span className="text-zinc-500">{timeAgo(c.created_at)}</span>
              {c.edited_at && <span className="text-zinc-600 text-xs">(edited)</span>}
            </div>

            {isEditing ? (
              <div className="mt-2">
                <textarea
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  rows={3}
                  maxLength={2000}
                  className="w-full bg-white/[0.02] border border-white/10 focus:border-white/30 rounded-xl px-3 py-2 text-sm text-white outline-none resize-y"
                />
                <div className="mt-2 flex items-center gap-2">
                  <button
                    onClick={() => onSaveEdit(c.id, editText)}
                    className="text-xs font-medium text-black bg-white px-3 py-1.5 rounded-full hover:scale-[1.04] transition-transform"
                  >
                    Save
                  </button>
                  <button
                    onClick={onCancelEdit}
                    className="text-xs text-zinc-500 hover:text-white transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <p className="mt-1 text-zinc-300 font-light leading-relaxed whitespace-pre-wrap break-words">
                {renderBody(c.body, me?.name)}
              </p>
            )}

            {/* Actions */}
            {!isEditing && (
              <div className="mt-2 flex items-center gap-4 text-xs text-zinc-500">
                {me && (
                  <button
                    onClick={() => onReply(isReplying ? null : c.id)}
                    data-testid={`comment-reply-${c.id}`}
                    className="inline-flex items-center gap-1 hover:text-white transition-colors"
                  >
                    <CornerDownRight className="w-3.5 h-3.5" strokeWidth={1.75} />
                    Reply
                  </button>
                )}
                {canEdit && (
                  <button
                    onClick={() => {
                      setEditText(c.body);
                      onEdit(c.id);
                    }}
                    data-testid={`comment-edit-${c.id}`}
                    className="inline-flex items-center gap-1 hover:text-white transition-colors"
                  >
                    <Pencil className="w-3.5 h-3.5" strokeWidth={1.75} />
                    Edit
                  </button>
                )}
                {canDelete && (
                  <button
                    onClick={() => onDelete(c.id)}
                    data-testid={`comment-delete-${c.id}`}
                    className="inline-flex items-center gap-1 hover:text-red-400 transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" strokeWidth={1.75} />
                    {isOwner ? "Delete" : "Remove"}
                  </button>
                )}
              </div>
            )}

            {/* Reply composer */}
            {isReplying && (
              <div className="mt-3">
                <textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  rows={2}
                  maxLength={2000}
                  placeholder={`Reply to @${c.author_name}…`}
                  className="w-full bg-white/[0.02] border border-white/10 focus:border-white/30 rounded-xl px-3 py-2 text-sm text-white placeholder:text-zinc-500 outline-none resize-y"
                />
                <div className="mt-2 flex items-center gap-2">
                  <button
                    disabled={posting || !replyText.trim()}
                    onClick={async () => {
                      await onSubmitReply(replyParentId, replyText);
                      setReplyText(isReply ? `@${c.author_name} ` : "");
                    }}
                    className="text-xs font-medium text-black bg-white px-3 py-1.5 rounded-full hover:scale-[1.04] transition-transform disabled:opacity-40"
                  >
                    Reply
                  </button>
                  <button
                    onClick={onCancelReply}
                    className="text-xs text-zinc-500 hover:text-white transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Replies */}
            {replies.length > 0 && (
              <div className="mt-5 space-y-5 pl-4 border-l border-white/10">
                {replies
                  .slice()
                  .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
                  .map((r) => (
                    <CommentNode
                      key={r.id}
                      comment={r}
                      replies={[]}
                      rootId={c.id}
                      me={me}
                      votes={votes}
                      vote={vote}
                      onReply={onReply}
                      replyTo={replyTo}
                      onCancelReply={onCancelReply}
                      onSubmitReply={onSubmitReply}
                      editing={editing}
                      onEdit={onEdit}
                      onCancelEdit={onCancelEdit}
                      onSaveEdit={onSaveEdit}
                      onDelete={onDelete}
                      posting={posting}
                      isReply
                    />
                  ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}
